import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import API_BASE_URL from '../api/Config';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [serviceSuggestions, setServiceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('authToken'));
  const { getCartCount } = useCart();
  const { getWishlistCount } = useWishlist();
  const cartCount = getCartCount();
  const wishlistCount = getWishlistCount();
  const searchRef = useRef(null);
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Close suggestions and clear search when route changes
  useEffect(() => {
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setServiceSuggestions([]);
  }, [location.pathname]);
  
  // Debounce search API calls
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        fetchSearchSuggestions(searchQuery.trim());
        fetchServiceSuggestions(searchQuery.trim());
      } else {
        setSearchSuggestions([]);
        setServiceSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutsideDrawer = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && 
          !event.target.closest('.hamburger-btn')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDrawer);
    return () => document.removeEventListener('mousedown', handleClickOutsideDrawer);
  }, []);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target) && 
          !event.target.closest('.search-toggle-btn')) {
        setShowSearch(false);
      }
    };

    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutsideSearch);
      return () => document.removeEventListener('mousedown', handleClickOutsideSearch);
    }
  }, [showSearch]);

  // Close search when scrolling (with debounce to prevent immediate close)
  useEffect(() => {
    if (!showSearch) return;

    let scrollTimeout;
    let lastScrollY = window.scrollY;
    const scrollThreshold = 5; // Minimum scroll distance to trigger close

    const handleScroll = () => {
      // Clear any existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Check if scroll distance exceeds threshold
      const scrollDistance = Math.abs(window.scrollY - lastScrollY);
      
      // Only close if user actually scrolled a meaningful amount
      if (scrollDistance > scrollThreshold) {
        // Debounce to prevent flickering on small scrolls
        scrollTimeout = setTimeout(() => {
          setShowSearch(false);
        }, 100);
      }
      
      lastScrollY = window.scrollY;
    };

    // Add a small delay before enabling scroll detection
    const enableScrollTimeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 300);

    return () => {
      clearTimeout(enableScrollTimeout);
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showSearch]);

  const fetchServiceSuggestions = (query) => {
    // Frontend service data
    const services = [
      {
        id: 'gypsum-ceiling',
        title: 'Gypsum Ceiling Solutions',
        description: 'Modern, elegant ceiling designs with premium gypsum materials',
        icon: '🏗️',
        route: '/gypsum-ceiling',
        type: 'service'
      },
     
      {
        id: 'installation',
        title: 'Installation Solutions',
        description: 'Expert installation services for all interior design elements',
        icon: '🔧',
        route: '/services',
        type: 'service'
      },
      {
        id: 'wainscoting',
        title: 'Wainscoting Panels',
        description: 'Decorative wall paneling that adds elegance and protection',
        icon: '🎨',
        route: '/wain-scouting',
        type: 'service'
      },
      {
        id: 'tv-gypsum-wall',
        title: 'TV & Gypsum Wall Units',
        description: 'Custom entertainment centers and feature walls',
        icon: '📺',
        route: '/tv-gypsum-wall',
        type: 'service'
      },
      {
        id: 'kitchen-wardrobe',
        title: 'Kitchen & Wardrobe Fitting',
        description: 'Professional fitting services for kitchens and wardrobes',
        icon: '🍳',
        route: '/kitchen-wardrobe-fitting',
        type: 'service'
      }
    ];

    // Filter services based on query
    const filtered = services.filter(service => 
      service.title.toLowerCase().includes(query.toLowerCase()) ||
      service.description.toLowerCase().includes(query.toLowerCase())
    );

    setServiceSuggestions(filtered);
    setShowSuggestions(true);
  };

  const fetchSearchSuggestions = async (query) => {
    try {
      setSearchLoading(true);
      const response = await fetch(`${API_BASE_URL}/products/search?name=${encodeURIComponent(query)}&limit=10`, {
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setSearchSuggestions(data.products || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error fetching search suggestions:', err);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Update login status when localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      setIsLoggedIn(!!localStorage.getItem('authToken'));
    };

    // Check on mount
    checkLoginStatus();

    // Listen for storage changes (in case of multiple tabs)
    window.addEventListener('storage', checkLoginStatus);
    
    // Create a custom event listener for same-window changes
    const handleAuthChange = () => checkLoginStatus();
    window.addEventListener('authChange', handleAuthChange);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to shop page with search query
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
      // Keep the search query in the input field
    }
  };

  const handleSuggestionClick = (item) => {
    if (item.type === 'service') {
      // Insert service title into search input for autocomplete
      setSearchQuery(item.title);
      // Navigate to service page
      navigate(item.route);
    } else {
      // Insert product name into search input for autocomplete
      setSearchQuery(item.name);
      // Navigate to product details page
      navigate(`/product/${item.id}`);
    }
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleNavClick = () => {
    setMenuOpen(false);
    setServicesOpen(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Desktop logo section - hidden on mobile */}
        <div className="header-left desktop-only">
          <Link to="/" className="logo">
            <img src="/techLogo.jpeg" alt="Techie Interior Logo" />
          <div className="header-title">
            <h4>Techie <span>Interior</span></h4>
          </div>
        </Link>

        </div>

        {/* Desktop search bar - hidden on mobile */}
        <div className="header-search desktop-only">
          <form className="search-form" onSubmit={handleSearch}>
            <input
              type="text"
              className="search-input"
              placeholder="Search products or Our services ..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
            />
            <button type="submit" className="search-btn" aria-label="Search">
              <SearchIcon fontSize="small" />
            </button>
          </form>
          
          {/* Search Suggestions Dropdown */}
          {showSuggestions && (
            <div className="search-suggestions">
              {searchLoading ? (
                <div className="suggestions-loading">Searching...</div>
              ) : (searchSuggestions.length > 0 || serviceSuggestions.length > 0) ? (
                <>
                  {/* Services Section */}
                  {serviceSuggestions.length > 0 && (
                    <>
                      <div className="suggestion-section-header">Services</div>
                      <ul className="suggestions-list">
                        {serviceSuggestions.map((service) => (
                          <li 
                            key={service.id} 
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(service)}
                          >
                            <div className="suggestion-image">
                              <span className="service-icon">{service.icon}</span>
                            </div>
                            <div className="suggestion-info">
                              <div className="suggestion-name">{service.title}</div>
                              <div className="suggestion-description">{service.description}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  
                  {/* Products Section */}
                  {searchSuggestions.length > 0 && (
                    <>
                      <div className="suggestion-section-header">Shop</div>
                      <ul className="suggestions-list">
                        {searchSuggestions.map((product) => (
                          <li 
                            key={product.id} 
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(product)}
                          >
                            <div className="suggestion-image">
                              <img 
                                src={product.image_fullpath?.[0] || product.image?.[0] || 'https://via.placeholder.com/50x50?text=No+Image'} 
                                alt={product.name}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                                }}
                              />
                            </div>
                            <div className="suggestion-info">
                              <div className="suggestion-name">{product.name}</div>
                              <div className="suggestion-price">KSh {product.price}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              ) : (
                <div className="no-suggestions">No products or services found</div>
              )}
            </div>
          )}
        </div>

        {/* ROW 1 — Top bar (Mobile only) */}
        <div className="header-top-bar">
          <div className="header-left">
          <button 
              className="hamburger-btn" 
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
            </button>
            <Link to="/" className="logo">
              <img src="/techLogo.jpeg" alt="Techie Interior Logo" />
            <div className="header-title">
              <h4>Techie <span >Interior</span> </h4>
            </div>
              </Link>

          </div>

          <div className="header-right-icons">
            <button className="nav-icon mobile-icon search-toggle-btn" onClick={() => setShowSearch(!showSearch)} title="Search" aria-label="Toggle Search">
              <SearchIcon />
            </button>
            <Link to="/wishlist" className="nav-icon mobile-icon" title="Wishlist" aria-label="View Wishlist">
              <FavoriteBorderOutlinedIcon />
              {wishlistCount > 0 && <span className="wishlist-badge">{wishlistCount}</span>}
            </Link>
            <Link to="/cart" className="nav-icon mobile-icon" title="Cart" aria-label="View Shopping Cart">
              <ShoppingCartOutlinedIcon />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>
            
          </div>
        </div>

        {/* ROW 2 — Search bar (conditionally displayed) */}
        {showSearch && (
        <div className="header-search-mobile" ref={searchRef}>
          <form className="search-form search-form-mobile" onSubmit={handleSearch}>
            <input
              type="text"
              className="search-input search-input-mobile"
              placeholder="Search products or Our services ..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
            />
            <button type="submit" className="search-btn search-btn-mobile" aria-label="Search">
              <SearchIcon fontSize="small" />
            </button>
          </form>
          
          {/* Search Suggestions Dropdown */}
          {showSuggestions && (
            <div className="search-suggestions">
              {searchLoading ? (
                <div className="suggestions-loading">Searching...</div>
              ) : (searchSuggestions.length > 0 || serviceSuggestions.length > 0) ? (
                <>
                  {/* Services Section */}
                  {serviceSuggestions.length > 0 && (
                    <>
                      <div className="suggestion-section-header">Services</div>
                      <ul className="suggestions-list">
                        {serviceSuggestions.map((service) => (
                          <li 
                            key={service.id} 
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(service)}
                          >
                            <div className="suggestion-image">
                              <span className="service-icon">{service.icon}</span>
                            </div>
                            <div className="suggestion-info">
                              <div className="suggestion-name">{service.title}</div>
                              <div className="suggestion-description">{service.description}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  
                  {/* Products Section */}
                  {searchSuggestions.length > 0 && (
                    <>
                      <div className="suggestion-section-header">Shop</div>
                      <ul className="suggestions-list">
                        {searchSuggestions.map((product) => (
                          <li 
                            key={product.id} 
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(product)}
                          >
                            <div className="suggestion-image">
                              <img 
                                src={product.image_fullpath?.[0] || product.image?.[0] || 'https://via.placeholder.com/50x50?text=No+Image'} 
                                alt={product.name}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                                }}
                              />
                            </div>
                            <div className="suggestion-info">
                              <div className="suggestion-name">{product.name}</div>
                              <div className="suggestion-price">KSh {product.price}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              ) : (
                <div className="no-suggestions">No products or services found</div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Desktop navigation - hidden on mobile */}
        <nav className="nav-menu desktop-only">
          <Link to="/" className="nav-item">Home</Link>
          <Link to="/shop" className="nav-item">Shop</Link>
          <div 
            className="nav-item dropdown"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <Link to="/services" className="nav-link">Our Services</Link>
            {servicesOpen && (
              <div className="dropdown-menu">
                <Link to="/gypsum-ceiling" className="dropdown-item">Gypsum Ceiling Solutions</Link>
                <Link to="/office-fitouts-kenya" className="dropdown-item">Office Fit-Outs Kenya</Link>
                <Link to="/services" className="dropdown-item">Installation Solutions</Link>
                <Link to="/wain-scouting" className="dropdown-item">Wain Scouting Panels</Link>
                <Link to="/tv-gypsum-wall" className="dropdown-item">Tv & Gypsum wall units</Link>
                <Link to="/kitchen-wardrobe-fitting" className="dropdown-item">Kitchen & wardrobe fitting</Link>
                <Link to="/interior-design-nairobi" className="dropdown-item">Interior Design Nairobi</Link>
              </div>
            )}
          </div>
          <Link to="/about-us" className="nav-item">About Us</Link>
          <Link to="/contact-us" className="nav-item">Contact Us</Link>
          <div className="nav-icons desktop-only">
            <Link to="/cart" className="nav-icon" title="Cart" aria-label="View Shopping Cart">
              <ShoppingCartOutlinedIcon />
              <span className="icon-label">Cart</span>
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>
            <Link to="/wishlist" className="nav-icon" title="Wishlist" aria-label="View Wishlist">
              <FavoriteBorderOutlinedIcon />
              <span className="icon-label">Wishlist</span>
              {wishlistCount > 0 && <span className="wishlist-badge">{wishlistCount}</span>}
            </Link>
            <Link to={isLoggedIn ? "/profile" : "/login"} className="nav-icon" title="Profile" aria-label="My Account">
              <PersonOutlineOutlinedIcon />
              <span className="icon-label">My Account</span>
            </Link>
          </div>
        </nav>

        {/* Mobile hamburger drawer */}
        <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`} ref={drawerRef}>
          <Link 
            to={isLoggedIn ? "/profile" : "/login"} 
            className="drawer-item drawer-account"
            onClick={handleNavClick}
          >
            <PersonOutlineOutlinedIcon className="drawer-icon" />
            <span>My Account</span>
          </Link>
          
          <Link to="/" className="drawer-item" onClick={handleNavClick}>
            <span className="drawer-icon">🏠</span>
            <span>Home</span>
          </Link>
          
          <Link to="/shop" className="drawer-item" onClick={handleNavClick}>
            <span className="drawer-icon">🛍️</span>
            <span>Shop</span>
          </Link>
          
          <div className="drawer-item drawer-services">
            <button
              type="button"
              className="drawer-services-header"
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              <span className="drawer-icon">⚙️</span>
              <span>Our Services</span>
              <span className={`drawer-chevron ${servicesOpen ? 'open' : ''}`}>›</span>
            </button>
            {servicesOpen && (
              <div className="drawer-services-submenu">
                <Link to="/gypsum-ceiling" className="drawer-subitem" onClick={handleNavClick}>Gypsum Ceiling Solutions</Link>
                <Link to="/services" className="drawer-subitem" onClick={handleNavClick}>Installation Solutions</Link>
                <Link to="/wain-scouting" className="drawer-subitem" onClick={handleNavClick}>Wain Scouting Panels</Link>
                <Link to="/tv-gypsum-wall" className="drawer-subitem" onClick={handleNavClick}>Tv & Gypsum wall units</Link>
                <Link to="/kitchen-wardrobe-fitting" className="drawer-subitem" onClick={handleNavClick}>Kitchen & wardrobe fitting</Link>
                <Link to="/office-fitouts-kenya" className="drawer-subitem" onClick={handleNavClick}>Office Fit-Outs Kenya</Link>
              </div>
            )}
          </div>
          
          <Link to="/about-us" className="drawer-item" onClick={handleNavClick}>
            <span className="drawer-icon">ℹ️</span>
            <span>About Us</span>
          </Link>
          
          <Link to="/contact-us" className="drawer-item" onClick={handleNavClick}>
            <span className="drawer-icon">📞</span>
            <span>Contact Us</span>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;